import { Phone } from "lucide-react";
import { WhatsAppButton } from "@/components/lunar/WhatsAppButton";
import { Reveal, Chapter } from "@/components/lunar/Reveal";
import { EventsEnquiryForm } from "./EnquiryForm";

export const EventsRemembers = () => (
    <section className="bg-[#F0EFEB]" data-testid="events-remembers-section">
        <div className="mx-auto max-w-6xl px-6 py-24 sm:py-28">
            <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                <Reveal>
                    <Chapter index="02" label="Lunar remembers" />
                    <h2 className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                        And Lunar remembers.
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-[#3E413B] sm:text-lg">
                        Months later, instead of wondering “Who was that investor I met at the
                        conference?”, attendees can see who they met, where they met and why the
                        connection mattered.
                    </p>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="rounded-3xl border border-[#E5E3DC] bg-[#F9F8F4] p-7 sm:p-9">
                        <p className="flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-[#899B6A]">
                            AI search of your network
                            <span className="rounded-full bg-[#D5D8CB] px-2.5 py-0.5 text-[9px] font-bold text-[#2C3618]">
                                COMING SOON
                            </span>
                        </p>
                        <p className="font-display mt-4 text-xl italic leading-snug text-[#3E413B] sm:text-2xl">
                            “Who did I meet who was looking to invest in hotels?”
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

const BUILT_BLOCKS = [
    { title: "For attendees", copy: "A simple way to find relevant people nearby." },
    {
        title: "For organisers",
        copy: "Event QR, intelligent matching and engagement analytics.",
    },
    {
        title: "In the room",
        copy: "Lunar signage, table cards and activation support.",
    },
];

export const EventsBuilt = ({ onCta }) => (
    <section className="mx-auto max-w-6xl px-6 py-24 sm:py-28" data-testid="events-built-section">
        <Reveal>
            <Chapter index="03" label="Built for your event" />
            <h2 className="font-display mt-5 max-w-2xl text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                Built for your event.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C5F58] sm:text-lg">
                From 50-person networking breakfasts to 5,000-person exhibitions.
            </p>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {BUILT_BLOCKS.map(({ title, copy }, i) => (
                <Reveal key={title} delay={i * 0.08}>
                    <div
                        data-testid={`events-built-${i + 1}`}
                        className="h-full rounded-3xl border border-[#E5E3DC] bg-[#F9F8F4] p-7 transition-colors duration-300 hover:border-[#899B6A]"
                    >
                        <h3 className="font-display text-xl font-semibold text-[#1D201A]">{title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-[#5C5F58]">{copy}</p>
                    </div>
                </Reveal>
            ))}
        </div>
        <Reveal delay={0.12}>
            <div className="mt-12 text-center">
                <button
                    data-testid="events-built-cta"
                    onClick={onCta}
                    className="inline-flex items-center rounded-full border border-[#2C3618] px-7 py-3.5 text-sm font-bold text-[#2C3618] transition-[background-color,color,transform] duration-300 hover:-translate-y-[2px] hover:bg-[#2C3618] hover:text-[#F9F8F4]"
                >
                    Plan your activation
                </button>
            </div>
        </Reveal>
    </section>
);

export const EventsClosing = () => (
    <section className="border-t border-[#E5E3DC]" data-testid="events-closing-section">
        <div className="mx-auto max-w-3xl px-6 py-24 sm:py-28">
            <Reveal className="text-center">
                <Chapter index="04" label="Talk to us" />
                <h2 className="font-display mt-5 text-[2.5rem] font-medium leading-[1.08] tracking-tight text-[#1D201A] sm:text-5xl">
                    Bring Lunar to <em className="italic text-[#4A5D23]">your event</em>.
                </h2>
                <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#5C5F58] sm:text-lg">
                    Tell us what you’re planning and we’ll shape the right Lunar activation for
                    your event.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <a
                        href="tel:+447810014529"
                        data-testid="events-call-button"
                        className="inline-flex items-center gap-2.5 rounded-full bg-[#2C3618] px-8 py-4 text-sm font-bold text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:-translate-y-[2px] hover:bg-[#4A5D23]"
                    >
                        <Phone className="h-4 w-4" strokeWidth={2} />
                        Call us
                    </a>
                    <WhatsAppButton testId="events-whatsapp-button" className="px-7 py-4" />
                </div>
                <p className="mt-4 text-sm text-[#5C5F58]">
                    Or complete the form and we’ll get back to you.
                </p>
            </Reveal>
            <EventsEnquiryForm />
            <Reveal delay={0.1}>
                <p className="font-display mt-16 text-center text-2xl font-medium italic text-[#4A5D23] sm:text-3xl">
                    Life should be done in person.
                </p>
            </Reveal>
        </div>
    </section>
);
